import apiClient from '@/lib/api';

// Mirror of back-end-next/src/lib/form-templates/constants.ts; template-labels.test.ts fails on drift.
//
// Admin service for the Templates section. Routes: back-end-next/src/app/api/v1/admin/form-templates.
// Design: docs/specs/2026-10-07-form-template-library-design.md

export type TemplateNiche =
  | 'home_services'
  | 'finance_insurance'
  | 'health'
  | 'marketing'
  | 'real_estate'
  | 'staffing'
  | 'b2b_services'
  | 'coaching'
  | 'fitness'
  | 'med_spa'
  | 'dental'
  | 'legal'
  | 'tutoring'
  | 'other';
export type TemplateGoal = 'book_calls' | 'capture_leads' | 'qualify';
export type TemplateAudience = 'high_ticket' | 'homeowners' | 'applicants' | 'business_owners';
export type TemplateStatus = 'draft' | 'published' | 'archived';
export type StatsWindow = 30 | 90 | 365;

export interface TemplateStatsDto {
  imports: number;
  agencies: number;
  copiesKept: number;
  eligible7d: number;
  publishedWithin7d: number;
  eligible14d: number;
  leadWithin14d: number;
  uniqueVisitors: number;
  completedLeads: number;
  /** Percent, 1 decimal, capped at 100. */
  completedOptInRate: number;
  bookings: number;
  /** Percent, same rounding. */
  bookingsPerVisitor: number;
  /** 1 decimal; null when no copy was published. */
  medianDaysToFirstPublish: number | null;
}

export interface TrialToPaid {
  agencies: number;
  paying: number;
  /** Percent, 1 decimal. */
  rate: number;
}

export interface SalesSummary {
  imports: number;
  agencies: number;
  templateUsers: TrialToPaid;
  allTrials: TrialToPaid;
}

/** What a version looks like to a customer; the thumbnails and theme are not rendered here. */
export interface TemplateCard {
  skin: string;
  questionCount: number;
  requiredCount: number;
  hasLogic: boolean;
  hasScoring: boolean;
  hasCalendarEnding: boolean;
  questions: unknown[];
  theme: unknown;
}

export interface AdminTemplateListItem {
  publicId: string;
  name: string;
  niche: TemplateNiche;
  goal: TemplateGoal;
  status: TemplateStatus;
  sortOrder: number;
  currentVersionNo: number | null;
  updatedAtUtc: string;
  stats: TemplateStatsDto;
}

export interface AdminTemplateList {
  days: StatsWindow;
  summary: SalesSummary;
  templates: AdminTemplateListItem[];
}

export interface AdminVersionDto {
  versionNo: number;
  changeNote: string | null;
  publishedAtUtc: string;
  publishedBy: { name: string } | null;
  isCurrent: boolean;
  card: TemplateCard;
  stats: TemplateStatsDto;
}

export interface AdminImportDto {
  createdAtUtc: string;
  agency: { publicId: string; name: string } | null;
  versionNo: number;
  published: boolean;
  leads: number;
  bookings: number;
  byStaff: boolean;
}

export interface AdminTemplateDetail {
  publicId: string;
  name: string;
  description: string;
  niche: TemplateNiche;
  goal: TemplateGoal;
  /** Who the form is for; the Templates page no longer shows it (the niche decides the shelf). */
  audience: TemplateAudience | null;
  /** Who it's for, under the name on the Templates page; "" when not set. */
  whoLine: string;
  /** What a qualified lead books, the pill on the card's picture; "" shows no pill. */
  bookingLabel: string;
  /** The card's picture; null draws the first question instead. Saved on upload, not part of a version. */
  coverUrl: string | null;
  whyItWorks: string[];
  status: TemplateStatus;
  sortOrder: number;
  evidence: string | null;
  studioFormPublicId: string | null;
  currentVersionNo: number | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  days: StatsWindow;
  stats: TemplateStatsDto;
  summary: SalesSummary;
  /** Newest first. */
  versions: AdminVersionDto[];
  /** The last 50. */
  imports: AdminImportDto[];
}

export interface TemplateCatalogueInput {
  name: string;
  description: string;
  niche: TemplateNiche;
  goal: TemplateGoal;
}

export interface TemplatePatch {
  name?: string;
  description?: string;
  niche?: TemplateNiche;
  goal?: TemplateGoal;
  /** null clears the group. */
  audience?: TemplateAudience | null;
  whoLine?: string;
  bookingLabel?: string;
  whyItWorks?: string[];
  sortOrder?: number;
  evidence?: string | null;
  status?: TemplateStatus;
}

/** Why a version cannot be published yet; mirrors the back end's PublishGateReason. */
export type PublishGateReason =
  | { code: 'NO_QUESTION' }
  | { code: 'PREPARE_DROPS'; elements: string[]; endings: string[]; logicRules: number; scoringRules: number }
  | { code: 'RICH_TEXT_STORAGE_URL'; count: number }
  | { code: 'MISSING_IMAGE'; count: number }
  | { code: 'TOO_MANY_IMAGES'; max: number };

export interface TemplateDefinitionCatalogue {
  name: string;
  description: string;
  niche: TemplateNiche;
  goal: TemplateGoal;
  /** Optional so older definitions still import (null and "" on the server). */
  audience?: TemplateAudience | null;
  whoLine?: string;
  bookingLabel?: string;
  whyItWorks: string[];
  evidence: string | null;
}

/** A template's catalogue with one version's snapshot. The snapshot is passed through, never read here. */
export interface TemplateDefinition {
  catalogue: TemplateDefinitionCatalogue;
  snapshot: unknown;
}

// Publishing and importing copy images, which can take longer than the 30 s default.
const SLOW = { timeout: 120_000 };

export const formTemplatesService = {
  async list(days: StatsWindow): Promise<AdminTemplateList> {
    const res = await apiClient.get('/admin/form-templates', { params: { days } });
    return res.data.data;
  },

  async detail(publicId: string, days: StatsWindow): Promise<AdminTemplateDetail | null> {
    try {
      const res = await apiClient.get(`/admin/form-templates/${publicId}`, { params: { days } });
      return res.data.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async create(input: TemplateCatalogueInput): Promise<{ publicId: string }> {
    const res = await apiClient.post('/admin/form-templates', input);
    return res.data.data;
  },

  /** The response body is ignored: the page refetches. */
  async update(publicId: string, patch: TemplatePatch): Promise<void> {
    await apiClient.patch(`/admin/form-templates/${publicId}`, patch);
  },

  async builderSession(publicId: string): Promise<{ builderUrl: string }> {
    const res = await apiClient.post(`/admin/form-templates/${publicId}/builder-session`);
    return res.data.data;
  },

  async publish(publicId: string, changeNote: string | null): Promise<{ version: AdminVersionDto }> {
    const res = await apiClient.post(
      `/admin/form-templates/${publicId}/publish`,
      changeNote ? { changeNote } : {},
      SLOW,
    );
    return res.data.data;
  },

  async exportVersion(publicId: string, versionNo: number): Promise<TemplateDefinition> {
    const res = await apiClient.get(`/admin/form-templates/${publicId}/versions/${versionNo}/export`);
    return res.data.data;
  },

  async importDefinition(definition: TemplateDefinition): Promise<{ publicId: string }> {
    const res = await apiClient.post('/admin/form-templates/import-definition', definition, SLOW);
    return res.data.data;
  },

  /** Sends the file as the raw body; the server checks it again. The response body is ignored: the page refetches. */
  async uploadTemplateCover(publicId: string, file: Blob): Promise<void> {
    await apiClient.put(`/admin/form-templates/${publicId}/cover`, file, {
      headers: { 'Content-Type': 'image/webp' },
      timeout: 60_000,
    });
  },

  /** The response body is ignored: the page refetches. */
  async removeTemplateCover(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/form-templates/${publicId}/cover`);
  },
};
